import React, { useEffect, useMemo, useState } from "react";
import { adminApi } from "../../lib/api";
import { BrandLogo } from "../../components/Brand";
import { Plus, Search, X, Save, Pencil } from "lucide-react";
import { ImgPicker, Field } from "./AdminBrands";

export default function AdminRewards() {
    const [brands, setBrands] = useState([]);
    const [rewards, setRewards] = useState([]);
    const [q, setQ] = useState("");
    const [brandFilter, setBrandFilter] = useState("");
    const [editing, setEditing] = useState(null);

    const load = async () => {
        const params = new URLSearchParams();
        if (q) params.set("q", q);
        if (brandFilter) params.set("brand_id", brandFilter);
        params.set("limit", "500");
        const [b, r] = await Promise.all([
            adminApi.get("/api/admin/brands?limit=500"),
            adminApi.get(`/api/admin/rewards?${params}`),
        ]);
        setBrands(b.data.brands);
        setRewards(r.data.rewards);
    };
    useEffect(() => { load(); }, [brandFilter]);

    const filtered = useMemo(() => {
        if (!q) return rewards;
        const t = q.toLowerCase();
        return rewards.filter((r) => (r.name || "").toLowerCase().includes(t) || (r.brand_name || "").toLowerCase().includes(t));
    }, [rewards, q]);

    return (
        <div>
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <h1 className="font-display text-3xl font-bold">Rewards ({rewards.length})</h1>
                <button onClick={() => setEditing("new")} className="fx-btn-primary" data-testid="new-reward-btn">
                    <Plus size={14} /> Add reward
                </button>
            </div>

            <div className="flex gap-2 mt-4 items-center flex-wrap">
                <div className="flex items-center gap-2 fx-card px-3 py-2 flex-1 min-w-[220px]">
                    <Search size={16} className="text-[#6b7280]" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search reward / brand" className="bg-transparent outline-none w-full text-sm" />
                </div>
                <select value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)} className="fx-input !w-auto">
                    <option value="">All brands</option>
                    {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-5">
                {filtered.slice(0, 200).map((r) => (
                    <div key={r.id} className={`fx-card p-4 ${r.active === false ? "opacity-50" : ""}`}>
                        <div className="flex items-center gap-3">
                            <BrandLogo url={r.brand_logo_url} fallback={r.fallback_logo_url} name={r.brand_name} size={44} />
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-semibold truncate">{r.brand_name}</p>
                                <p className="text-[11px] text-[#6b7280] truncate">{r.category} · {r.type}</p>
                            </div>
                            <button onClick={() => setEditing(r)} className="fx-btn-ghost !py-1.5"><Pencil size={12} /></button>
                        </div>
                        <p className="text-xs mt-3"><b>Denominations:</b> {(r.denominations || []).join(", ")}</p>
                        <p className="text-xs"><b>Points/₹:</b> {r.points_per_inr} · <b>Discount:</b> {r.discount_percent}% · <b>Min:</b> ₹{r.min_value}</p>
                        {r.image_url && <img src={r.image_url} alt="" className="mt-2 rounded-lg max-h-24 object-cover" />}
                    </div>
                ))}
            </div>

            {editing && (
                <RewardEditor reward={editing === "new" ? null : editing} brands={brands} onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); await load(); }} />
            )}
        </div>
    );
}

function RewardEditor({ reward, brands, onClose, onSaved }) {
    const isNew = !reward;
    const [form, setForm] = useState({
        brand_id: reward?.brand_id || (brands[0]?.id || ""),
        type: reward?.type || "gift_card",
        name: reward?.name || (brands[0]?.name ? `${brands[0]?.name} Gift Card` : ""),
        description: reward?.description || "",
        denominations: (reward?.denominations || [500, 1000, 2000, 5000]).join(","),
        min_value: reward?.min_value || 500,
        max_value: reward?.max_value || 10000,
        points_per_inr: reward?.points_per_inr || 4,
        discount_percent: reward?.discount_percent || 0,
        points_required: reward?.points_required || "",
        validity: reward?.validity || "1 Year",
        redemption_type: reward?.redemption_type || "Both",
        terms: reward?.terms || "",
        featured: !!reward?.featured,
        active: reward?.active !== false,
        image_url: reward?.image_url || "",
        gallery_urls: (reward?.gallery_urls || []).join("\n"),
    });
    const [busy, setBusy] = useState(false);

    const uploadImage = async (file, kind) => {
        const b64 = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result.split(",")[1]); r.readAsDataURL(file); });
        const r = await adminApi.post("/api/admin/upload-image", { file_base64: b64, mime_type: file.type, kind });
        return r.data.url;
    };

    const save = async () => {
        setBusy(true);
        try {
            const payload = {
                ...form,
                denominations: form.denominations.split(",").map((x) => parseInt(x.trim())).filter(Boolean),
                min_value: parseInt(form.min_value) || 100,
                max_value: parseInt(form.max_value) || 10000,
                points_per_inr: parseFloat(form.points_per_inr) || 4,
                discount_percent: parseFloat(form.discount_percent) || 0,
                points_required: form.points_required ? parseInt(form.points_required) : null,
                gallery_urls: form.gallery_urls.split("\n").map((s) => s.trim()).filter(Boolean),
            };
            if (isNew) {
                await adminApi.post("/api/admin/rewards", payload);
            } else {
                delete payload.brand_id;
                delete payload.type;
                await adminApi.patch(`/api/admin/rewards/${reward.id}`, payload);
            }
            onSaved();
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-5">
                    <h2 className="font-display text-xl font-bold">{isNew ? "Add reward / voucher" : `Edit · ${reward.brand_name}`}</h2>
                    <button onClick={onClose}><X size={20} /></button>
                </div>

                <div className="grid md:grid-cols-2 gap-3">
                    {isNew && (
                        <Field label="Brand *">
                            <select value={form.brand_id} onChange={(e) => setForm({ ...form, brand_id: e.target.value })} className="fx-input">
                                {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                            </select>
                        </Field>
                    )}
                    <Field label="Reward name *"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="fx-input" /></Field>
                    <Field label="Type">
                        <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="fx-input" disabled={!isNew}>
                            <option value="gift_card">Gift card</option>
                            <option value="voucher">Voucher</option>
                            <option value="product">Product</option>
                            <option value="experience">Experience</option>
                            <option value="subscription">Subscription</option>
                        </select>
                    </Field>
                    <Field label="Description" wide><textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="fx-input" /></Field>

                    <Field label="Denominations (comma-sep)"><input value={form.denominations} onChange={(e) => setForm({ ...form, denominations: e.target.value })} placeholder="500,1000,2000" className="fx-input" /></Field>
                    <Field label="Min value ₹"><input type="number" value={form.min_value} onChange={(e) => setForm({ ...form, min_value: e.target.value })} className="fx-input" /></Field>
                    <Field label="Max value ₹"><input type="number" value={form.max_value} onChange={(e) => setForm({ ...form, max_value: e.target.value })} className="fx-input" /></Field>
                    <Field label="Discount %"><input type="number" step="0.1" value={form.discount_percent} onChange={(e) => setForm({ ...form, discount_percent: e.target.value })} className="fx-input" /></Field>
                    <Field label="Points per ₹"><input type="number" step="0.1" value={form.points_per_inr} onChange={(e) => setForm({ ...form, points_per_inr: e.target.value })} className="fx-input" /></Field>
                    <Field label="Points required (full-points price for min denom)"><input type="number" value={form.points_required} onChange={(e) => setForm({ ...form, points_required: e.target.value })} placeholder="optional, e.g. 2000" className="fx-input" /></Field>
                    <Field label="Validity"><input value={form.validity} onChange={(e) => setForm({ ...form, validity: e.target.value })} className="fx-input" /></Field>
                    <Field label="Redemption">
                        <select value={form.redemption_type} onChange={(e) => setForm({ ...form, redemption_type: e.target.value })} className="fx-input">
                            <option value="Both">Online + In-store</option>
                            <option value="Online">Online only</option>
                            <option value="Offline">In-store only</option>
                        </select>
                    </Field>
                    <Field label="Terms & conditions" wide><textarea rows={3} value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} className="fx-input" /></Field>

                    <ImgPicker label="Voucher image" value={form.image_url}
                        onUpload={async (f) => { const u = await uploadImage(f, "voucher"); setForm({ ...form, image_url: u }); }}
                        onUrl={(u) => setForm({ ...form, image_url: u })} />
                    <Field label="Gallery URLs (one per line)" wide><textarea rows={3} value={form.gallery_urls} onChange={(e) => setForm({ ...form, gallery_urls: e.target.value })} className="fx-input" /></Field>
                </div>

                <div className="mt-4 flex items-center gap-4">
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> Featured</label>
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active</label>
                    <button onClick={save} disabled={busy || !form.name} className="fx-btn-primary ml-auto" data-testid="save-reward-btn">
                        <Save size={14} /> {isNew ? "Create reward" : "Save changes"}
                    </button>
                </div>
            </div>
        </div>
    );
}
